import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { OrderService } from './order.service';
import { StorageKeys } from './storage-keys';
import { StorageService } from './storage.service';
import {
  DurableOperationIdentity,
  DurableOperationIdentityService,
} from './durable-operation-identity.service';

export type CashOrderOperationState =
  | 'PREPARED'
  | 'SUBMITTING'
  | 'CONFIRMED';

export interface CashOrderOperation {
  version: 1;
  identity: DurableOperationIdentity;
  operationId: string;
  state: CashOrderOperationState;
  payload: any;
  orderId: number | null;
  createdAt: number;
  updatedAt: number;
}

/**
 * Durable cash-order boundary.
 *
 * The exact order payload (including its `uuid`) is stored before the HTTP
 * request. Every retry after a timeout or WebView restart therefore reaches
 * the backend with the same idempotency key.
 */
@Injectable({ providedIn: 'root' })
export class CashOrderOperationService {
  private submitInFlight: Promise<number> | null = null;

  constructor(
    private storage: StorageService,
    private orders: OrderService,
    private identity: DurableOperationIdentityService,
  ) {}

  async getActive(): Promise<CashOrderOperation | null> {
    const stored = await this.storage.getItemStrict<unknown>(
      StorageKeys.CASH_ORDER_OPERATION,
    );
    if (stored === null) {
      return null;
    }
    this.assertValid(stored);
    await this.identity.assertCurrent(stored.identity);
    return this.clone(stored);
  }

  async submit(payload?: any): Promise<number> {
    if (this.submitInFlight) {
      return this.submitInFlight;
    }
    this.submitInFlight = this.submitInternal(payload);
    try {
      return await this.submitInFlight;
    } finally {
      this.submitInFlight = null;
    }
  }

  async acknowledgeOrder(orderId: number): Promise<void> {
    const active = await this.getActive();
    if (!active) {
      return;
    }
    if (active.state !== 'CONFIRMED' || active.orderId !== orderId) {
      throw new Error('CASH_ORDER_ACK_MISMATCH');
    }
    await this.storage.removeItemStrict(StorageKeys.CASH_ORDER_OPERATION);
    if (await this.storage.getItemStrict(StorageKeys.CASH_ORDER_OPERATION)) {
      throw new Error('CASH_ORDER_ACK_FAILED');
    }
  }

  private async submitInternal(payload?: any): Promise<number> {
    let operation = await this.getActive();
    if (!operation) {
      if (!payload) {
        throw new Error('NO_CASH_ORDER_TO_RECOVER');
      }
      operation = this.create(payload, await this.identity.captureCurrent());
      await this.persistVerified(operation);
    } else if (
      payload &&
      this.canonical(payload) !== this.canonical(operation.payload)
    ) {
      throw new Error('ANOTHER_CASH_ORDER_IS_PENDING');
    }

    if (operation.state === 'CONFIRMED') {
      return operation.orderId as number;
    }

    operation = {
      ...operation,
      state: 'SUBMITTING',
      updatedAt: Date.now(),
    };
    await this.persistVerified(operation);
    await this.identity.assertCurrent(operation.identity);

    const response: any = await firstValueFrom(
      this.orders.saveOrder(this.clone(operation.payload)),
    );
    const orderId = Number(response?.id ?? response);
    if (!Number.isSafeInteger(orderId) || orderId <= 0) {
      throw new Error('CASH_ORDER_RESPONSE_HAS_NO_ID');
    }

    operation = {
      ...operation,
      state: 'CONFIRMED',
      orderId,
      updatedAt: Date.now(),
    };
    await this.persistVerified(operation);
    return orderId;
  }

  private create(
    payload: any,
    identity: DurableOperationIdentity,
  ): CashOrderOperation {
    const frozenPayload = this.clone(payload);
    const uuid = `${frozenPayload?.uuid || ''}`.trim();
    if (!uuid || !uuid.startsWith('idmp-')) {
      throw new Error('CASH_ORDER_REQUIRES_STABLE_UUID');
    }
    const now = Date.now();
    return {
      version: 1,
      identity,
      operationId: `cash-${uuid}`,
      state: 'PREPARED',
      payload: frozenPayload,
      orderId: null,
      createdAt: now,
      updatedAt: now,
    };
  }

  private async persistVerified(
    operation: CashOrderOperation,
  ): Promise<void> {
    this.assertValid(operation);
    await this.storage.setItem(StorageKeys.CASH_ORDER_OPERATION, operation);
    const stored = await this.storage.getItemStrict<unknown>(
      StorageKeys.CASH_ORDER_OPERATION,
    );
    if (this.canonical(stored) !== this.canonical(operation)) {
      throw new Error('CASH_ORDER_JOURNAL_WRITE_FAILED');
    }
  }

  private assertValid(value: unknown): asserts value is CashOrderOperation {
    const item = value as any;
    if (
      !item ||
      item.version !== 1 ||
      (() => {
        try {
          this.identity.assertShape(item.identity);
          return false;
        } catch {
          return true;
        }
      })() ||
      !['PREPARED', 'SUBMITTING', 'CONFIRMED'].includes(item.state) ||
      typeof item.operationId !== 'string' ||
      !item.operationId ||
      !item.payload ||
      typeof item.payload !== 'object' ||
      typeof item.payload.uuid !== 'string' ||
      !item.payload.uuid.startsWith('idmp-') ||
      !Number.isSafeInteger(item.createdAt) ||
      !Number.isSafeInteger(item.updatedAt) ||
      (item.state === 'CONFIRMED' &&
        (!Number.isSafeInteger(item.orderId) || item.orderId <= 0)) ||
      (item.state !== 'CONFIRMED' && item.orderId !== null)
    ) {
      throw new Error('INVALID_CASH_ORDER_JOURNAL');
    }
  }

  private canonical(value: unknown): string {
    return JSON.stringify(value, (_key, current) => {
      if (
        current &&
        typeof current === 'object' &&
        !Array.isArray(current)
      ) {
        return Object.keys(current)
          .sort()
          .reduce((sorted: Record<string, unknown>, key) => {
            sorted[key] = current[key];
            return sorted;
          }, {});
      }
      return current;
    });
  }

  private clone<T>(value: T): T {
    return JSON.parse(JSON.stringify(value));
  }
}
